import { useCallback, useEffect, useState } from 'react';

import { ONE_TIME_SHOW_KEYS } from '@/hooks/one-time-show-keys';
import { read, save } from '@/utils/async-storage';
import { create } from 'zustand';

export type OneTimeShowKeys = (typeof ONE_TIME_SHOW_KEYS)[number];

/**
 * Predecessors for tooltips, that could prevent a tooltip from showing, while the other tooltips are shown.
 */
const OneTimeShowPredecessors: Partial<Record<OneTimeShowKeys, OneTimeShowKeys[]>> = {
    Pickem_SearchTab_FeatureTooltip: ['Pickem_FeedTab_FeatureTooltip'],
};

type OneTimeShowStore = {
    show: Partial<Record<OneTimeShowKeys, boolean>>;
    setShow: (key: OneTimeShowKeys, value: boolean) => void;
};

export const useOneTimeShowStore = create<OneTimeShowStore>(set => ({
    show: {},
    setShow: (key, value) => set(state => ({ show: { ...state.show, [key]: value } })),
}));

export const useShowOneTime = (storageKey: OneTimeShowKeys) => {
    const allTooltipsShowData = useOneTimeShowStore(s => s.show);
    const show = allTooltipsShowData[storageKey] ?? false;

    const setShow = useOneTimeShowStore(s => s.setShow);

    const [allPredecessorsShown, setAllPredecessorsShown] = useState(false);

    useEffect(() => {
        const checkShown = async () => {
            const hasBeenShown = await readStorageHasBeenShown(storageKey);

            const predecessors = OneTimeShowPredecessors[storageKey] ?? [];
            const allPredecShown = (await Promise.all(predecessors.map(it => readStorageHasBeenShown(it)))).every(
                it => it === true
            );
            setAllPredecessorsShown(allPredecShown);

            if (!hasBeenShown && !show) {
                setShow(storageKey, true);
            }
        };

        checkShown();
    }, [storageKey, setShow, allTooltipsShowData, show, allPredecessorsShown]);

    const handleInteraction = useCallback(async () => {
        await writeStorageHasBeenShown(storageKey);
        setShow(storageKey, false);
    }, [storageKey, setShow]);

    return { show: show && allPredecessorsShown, handleInteraction };
};

async function readStorageHasBeenShown(storageKey: string): Promise<boolean> {
    const hasBeenShown = await read(storageKey);
    if (hasBeenShown === null || (typeof hasBeenShown === 'boolean' && !hasBeenShown)) {
        return false;
    }
    return true;
}

async function writeStorageHasBeenShown(storageKey: string): Promise<void> {
    return save(storageKey, true);
}
